import { useRef, useState } from "react";
import { toast } from "sonner";
import { AlignLeft, AlignCenter, AlignRight, ImagePlus, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { FieldWrap, NativeSelect } from "@/components/Field";
import { OnOffSwitch } from "@/components/OnOffSwitch";
import { api, errMsg } from "@/lib/api";
import { FONTS, SIZES, HEIGHT_OPTIONS, DIVIDER_STYLES, SWATCHES, publicImage } from "@/lib/content";

function ColorField({ label, value, onChange, testid }) {
  return (
    <FieldWrap label={label}>
      <div className="flex flex-wrap items-center gap-1.5">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-10 w-12 cursor-pointer rounded-md border bg-white p-1" data-testid={testid} />
        {SWATCHES.map((c) => (
          <button key={c} type="button" onClick={() => onChange(c)} title={c} data-testid={`${testid}-swatch-${c.slice(1)}`}
            className={`h-7 w-7 rounded-full border-2 ${value.toLowerCase() === c ? "border-[hsl(var(--gold))] ring-2 ring-[hsl(var(--gold))]/40" : "border-slate-300"}`} style={{ background: c }} />
        ))}
      </div>
    </FieldWrap>
  );
}

function AlignButtons({ value, onChange, testid }) {
  const opts = [["left", AlignLeft, "Left"], ["center", AlignCenter, "Center"], ["right", AlignRight, "Right"]];
  return (
    <FieldWrap label="Text alignment">
      <div className="inline-flex rounded-lg border border-slate-300 bg-white p-1">
        {opts.map(([k, Icon, l]) => (
          <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold ${value === k ? "bg-[hsl(var(--primary))] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <Icon className="h-4 w-4" />{l}
          </button>
        ))}
      </div>
    </FieldWrap>
  );
}

function ImageField({ value, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/content/images", fd);
      onChange(r.data.id);
      toast.success("Image uploaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <FieldWrap label="Picture">
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testid}-input`} />
      <div className="flex items-center gap-3">
        {value && <img src={publicImage(value)} alt="" className="h-16 w-24 rounded-md border object-cover" />}
        <Button type="button" variant="outline" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-upload`}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-2 h-4 w-4" />}{value ? "Replace picture" : "Upload picture"}
        </Button>
        {value && <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)} data-testid={`${testid}-remove`}><X className="mr-1 h-4 w-4" />Remove</Button>}
      </div>
    </FieldWrap>
  );
}

export function BlockEditor({ block: b, onChange }) {
  const set = (k) => (v) => onChange({ [k]: v?.target ? v.target.value : v });
  const t = `block-editor-${b.id}`;
  const hasText = b.type === "banner" || b.type === "text";
  return (
    <div className="mt-3 space-y-4 border-t border-slate-200 pt-4" data-testid={t}>
      {hasText && <FieldWrap label="Heading"><Input className="h-11" value={b.title} onChange={set("title")} data-testid={`${t}-title`} /></FieldWrap>}
      {b.type === "image" && <FieldWrap label="Caption (optional)"><Input className="h-11" value={b.title} onChange={set("title")} data-testid={`${t}-title`} /></FieldWrap>}
      {hasText && <FieldWrap label="Text"><Textarea rows={4} value={b.body} onChange={set("body")} data-testid={`${t}-body`} /></FieldWrap>}
      {(b.type === "banner" || b.type === "image") && <ImageField value={b.image_id} onChange={set("image_id")} testid={`${t}-image`} />}
      {b.type !== "divider" && (
        <div className="grid gap-4 sm:grid-cols-2">
          <FieldWrap label="Font style">
            <select value={b.font} onChange={set("font")} className="h-11 w-full rounded-lg border border-input bg-white px-3" style={{ fontFamily: b.font }} data-testid={`${t}-font`}>
              {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
            </select>
          </FieldWrap>
          <FieldWrap label="Font size"><NativeSelect value={b.size} onChange={set("size")} options={SIZES} testid={`${t}-size`} /></FieldWrap>
        </div>
      )}
      {b.type !== "divider" && <AlignButtons value={b.align} onChange={set("align")} testid={`${t}-align`} />}
      {b.type === "banner" && <FieldWrap label="Banner height"><NativeSelect value={b.height} onChange={set("height")} options={HEIGHT_OPTIONS} testid={`${t}-height`} /></FieldWrap>}
      {b.type === "divider" && <FieldWrap label="Line style"><NativeSelect value={b.divider_style} onChange={set("divider_style")} options={DIVIDER_STYLES} testid={`${t}-divider-style`} /></FieldWrap>}
      <ColorField label={b.type === "divider" ? "Line color" : "Text color"} value={b.color} onChange={set("color")} testid={`${t}-color`} />
      <ColorField label="Background color" value={b.bg} onChange={set("bg")} testid={`${t}-bg`} />
      <label className="flex items-center justify-between rounded-lg border bg-white p-3 text-sm font-semibold">
        Show this section on the page
        <OnOffSwitch checked={b.visible} onChange={set("visible")} testid={`${t}-visible`} />
      </label>
    </div>
  );
}
